/**
 * Geteilte, gecachte Datenquelle für das AIFieldMenu-Dropdown.
 *
 * field-map / System-Tools / Meine-Vorlagen sind workspace- bzw. tool-weit
 * identisch — daher per React-Query-Cache (stabile Keys) nur EINMAL geladen
 * und über alle AIFieldMenu-Instanzen geteilt, statt bei jedem Öffnen neu.
 */
import { useQuery } from '@tanstack/react-query';
import {
  fetchFieldMap,
  listWorkspaceTemplates,
  fetchTextTools,
  type TextTemplate,
  type TextToolInfo,
} from '@/components/v2/tools/text/textGenerationApi';
import { getAIReferenceImages } from '@/lib/api/products';

export const AI_FIELD_MAP_KEY = ['text-field-map'] as const;
export const AI_FIELD_TOOLS_KEY = ['text-tools'] as const;
export const aiFieldTemplatesKey = (toolCode?: string) =>
  ['text-workspace-templates', toolCode] as const;

export interface AIFieldMenuData {
  templates: TextTemplate[];
  tools: TextToolInfo[];
  vision: boolean;
  imageKey: string | undefined;
}

export function useAIFieldMenuData(fieldType: string, productId: string): AIFieldMenuData {
  const { data: fieldMap } = useQuery({
    queryKey: AI_FIELD_MAP_KEY,
    queryFn: fetchFieldMap,
  });

  const entry = fieldMap?.find((e) => e.field_type === fieldType);
  const toolCode = entry?.tool_code;

  // System-Tools sind global (kein toolCode) — gleicher Key wie der Text-Hub,
  // damit der Cache projektweit geteilt wird.
  const { data: tools } = useQuery({
    queryKey: AI_FIELD_TOOLS_KEY,
    queryFn: fetchTextTools,
  });

  // staleTime: 0 → gecachte Liste sofort sichtbar (Bug behoben), aber bei
  // jedem Mount im Hintergrund revalidiert, damit anderswo neu angelegte
  // Vorlagen ohne 5-Min-Verzögerung erscheinen (stale-while-revalidate).
  const { data: templates } = useQuery({
    queryKey: aiFieldTemplatesKey(toolCode),
    queryFn: () => listWorkspaceTemplates(toolCode as string),
    enabled: !!toolCode,
    staleTime: 0,
  });

  const { data: refs } = useQuery({
    queryKey: ['ai-reference-images', productId],
    queryFn: () => getAIReferenceImages(productId),
    enabled: !!entry?.vision && !!productId,
  });

  const slot = refs?.slots?.find((s) => !s.is_empty && s.asset_id);

  return {
    templates: templates ?? [],
    tools: tools ?? [],
    vision: Boolean(entry?.vision && slot),
    imageKey: slot?.asset_id,
  };
}
